import { useGetErrorInfo } from '@thoughtspot/blink-context';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { translate, useTranslation } from '@thoughtspot/i18n';
import { create } from '@thoughtspot/logger';
import { BannerAlertType } from '@thoughtspot/radiant-react/widgets/alert';
import {
    getUserIdToActivationTokenForNebula,
    isNebulaDevCluster,
    setUserIdToActivationTokenForNebula,
} from '@thoughtspot/session-service';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useAppContext } from '/@contexts/global-app-context';
import { getTranslatedInfoForMessageCode } from '/@utils/error-handling/error-strings-mapping';
import { useInviteUsersMutation } from '../../../services/trial/trial-service';
import { shouldShowDisplayNameDialog } from '../../user-preference/display-name/display-name.util';
import {
    InviteUsersContainerProps,
    UpdateDisplayNameDialogOrigin,
} from '../invite-users.interface';
import { InviteUsersModal } from '../invite-users-modal/invite-users-modal';
import { UpdateDisplayNameContainer } from '../update-display-name/update-display-name.container';

const logger = create('invite-users-container');

export const InviteUsersContainer: React.FC<InviteUsersContainerProps> = ({
    showModals = false,
    onEmailAdd,
    onInviteComplete = _.noop,
    onModalCloseCB = _.noop,
    isCopilotEnabled = false,
    source,
}) => {
    const { t } = useTranslation();
    const [isInviteModalOpen, setIsInviteModalOpen] = useState<boolean>(false);
    const [isToastVisible, setIsToastVisible] = React.useState(false);

    const [
        isUpdateDisplayNameModalOpen,
        setIsUpdateDisplayNameModalOpen,
    ] = useState<boolean>(false);
    const [errorMsg, setErrMsg] = useState('');
    const {
        systemConfigService,
        sessionService,
        metricsService,
    } = useAppContext();
    const supportEmail: string = !_.isEmpty(
        systemConfigService.getSupportEmail(),
    )
        ? systemConfigService.getSupportEmail()
        : systemConfigService.getAdminEmail();
    const [isBannerVisible, setIsBannerVisible] = React.useState(false);

    const {
        data: InviteUserInfo,
        loading: inviteLoading,
        inviteUsers,
    } = useInviteUsersMutation(onInviteComplete);

    // Get any error obj set from parent Error Boundaries
    const { errors } = useGetErrorInfo();
    // Set showGlobalError to true when even the error object changes
    useEffect(() => {
        if (errors.length) {
            const upstreamErrorCode =
                errors?.[0].errorObj.extensions?.exception?.upstreamResponse
                    ?.data?.code;
            // Keys for error message in case of API errors
            const translatedErrMsgKeys = getTranslatedInfoForMessageCode(
                // Prioritise upstreamErrorCode over Generic UpstreamError code
                upstreamErrorCode || errors?.[0]?.code,
                'homehub.invite_others.error_message',
            );
            setErrMsg(translate(translatedErrMsgKeys.summary as any));
        }
    }, [errors]);

    useEffect(() => {
        if (InviteUserInfo) {
            sessionService.triggerGlobalBannerObserver();
        }
    }, [inviteLoading, !!InviteUserInfo]);

    const onModalConfirm = async (
        invitedUsers: string[],
        description: string,
    ) => {
        let response;
        try {
            response = await inviteUsers(invitedUsers, description, false);
            if (isNebulaDevCluster()) {
                // save userIdToToken in pref. key
                const userInfo = response?.data?.Trial__inviteUsers;
                const activationUrls = userInfo?.activationUrls;
                const userIds = userInfo?.userIds;
                const userIdToToken: Map<
                    string,
                    string
                > = getUserIdToActivationTokenForNebula()
                    ? getUserIdToActivationTokenForNebula()
                    : new Map<string, string>();
                activationUrls.forEach((actUrl: string, index: number) => {
                    const urlparams = new URLSearchParams(actUrl);
                    userIdToToken.set(userIds[index], urlparams.get('token'));
                });

                setUserIdToActivationTokenForNebula(userIdToToken);
            }
        } catch (error) {
            setIsBannerVisible(true);
            logger.error('error calling invite api', error);
        } finally {
            setIsInviteModalOpen(false);
            onModalCloseCB();
        }
        return response?.data?.Trial__inviteUsers;
    };

    const onModalClose = () => {
        setIsInviteModalOpen(false);
        if (onModalCloseCB) {
            onModalCloseCB();
        }
    };

    useEffect(() => {
        if (showModals) {
            if (shouldShowDisplayNameDialog()) {
                setIsUpdateDisplayNameModalOpen(true);
            } else {
                setIsInviteModalOpen(true);
            }
        }
    }, [showModals]);

    return (
        <>
            <GlobalAlert.Banner
                isVisible={isBannerVisible}
                message={errorMsg}
                type={BannerAlertType.ERROR}
                onClose={() => setIsBannerVisible(false)}
            />
            <GlobalAlert.Toast
                isVisible={isToastVisible}
                message={t('homehub.invite_others.success_message')}
                onClose={() => setIsToastVisible(false)}
                data-testid="GlobalAlert-toast"
                autoHide
            />
            {isUpdateDisplayNameModalOpen && (
                <UpdateDisplayNameContainer
                    isUpdateDisplayNameModalOpen={isUpdateDisplayNameModalOpen}
                    setIsUpdateDisplayNameModalOpen={
                        setIsUpdateDisplayNameModalOpen
                    }
                    onUpdateCB={() => setIsInviteModalOpen(true)}
                    onDismissCB={() => setIsInviteModalOpen(true)}
                    origin={UpdateDisplayNameDialogOrigin.INVITE}
                />
            )}
            {isInviteModalOpen && (
                <InviteUsersModal
                    isModalOpen={isInviteModalOpen}
                    onModalClose={onModalClose}
                    onModalConfirm={onModalConfirm}
                    onEmailAdd={onEmailAdd}
                    supportEmail={supportEmail}
                    inviteModalType={sessionService.getInviteModalType()}
                    collectEvent={metricsService.collectEvent}
                    errorMsg={errorMsg}
                    setIsToastVisible={setIsToastVisible}
                    isCopilotEnabled={isCopilotEnabled}
                    source={source}
                />
            )}
        </>
    );
};
